import { compressImage } from '@/features/atendimento/lib/compress-image';
import { getSocket } from '@/features/atendimento/socket';
import { ChangePasswordModal } from '@/features/auth/ChangePasswordModal';
import { logoutRequest, revokeAllSessionsRequest } from '@/features/auth/api';
import {
  useMyAvatar,
  useUpdatePreferences,
  useUploadAvatar,
  useUserPreferences,
} from '@/features/auth/preferences';
import { InternalChatButton } from '@/features/internal-chat/components/InternalChatPopup';
import { NotificationSettingsModal } from '@/features/notifications/components/NotificationSettingsModal';
import { ChannelHealthBadge } from '@/shared/components/layout/ChannelHealthBadge';
import { TopNav } from '@/shared/components/layout/TopNav';
import { VersionFooter } from '@/shared/components/layout/VersionFooter';
import { Logo } from '@/shared/components/ui/Logo';
import { useLarguraAbaixoDe } from '@/shared/hooks/useLarguraAbaixoDe';
import { broadcastLogout } from '@/shared/lib/auth-broadcast';
import { confirm } from '@/shared/lib/confirm';
import { clearPersistedQueryCache } from '@/shared/lib/query-cache';
import { useAuthStore } from '@/shared/stores/auth-store';
import { useThemeStore } from '@/shared/stores/theme-store';
import { useRouter } from '@tanstack/react-router';
import {
  BellRing,
  Camera,
  ChevronDown,
  KeyRound,
  Loader2,
  LogOut,
  Moon,
  ShieldOff,
  Sun,
  User,
  UserCheck,
  UserX,
} from 'lucide-react';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

/** Abaixo desta largura o menu do topo vira os três pontinhos (TopNav
 *  `compacto`). Mesmo corte do Atendimento em uma coluna — é o "modo
 *  responsivo" do painel inteiro. */
const LARGURA_TOPO_COMPACTO = 900;

export function Topbar() {
  const compacto = useLarguraAbaixoDe(LARGURA_TOPO_COMPACTO);
  const user = useAuthStore((s) => s.user);
  const themeResolved = useThemeStore((s) => s.resolved);
  const setThemePref = useThemeStore((s) => s.setPref);
  const updatePrefs = useUpdatePreferences();
  const prefsQ = useUserPreferences();
  const avatarQ = useMyAvatar();
  const avatarUrl = avatarQ.data?.avatar_url ?? null;
  const uploadAvatar = useUploadAvatar();
  // Disponibilidade manual do atendente. 'offline' = não recebe distribuição
  // automática e aparece offline pro admin (mas segue usando o sistema).
  // Default disponível quando ausente. Persistido (cross-device, sobrevive F5).
  const available = prefsQ.data?.availability !== 'offline';
  const setAvailability = (value: 'online' | 'offline'): void => {
    updatePrefs.mutate({ availability: value });
  };
  const onPickAvatar = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-selecionar o mesmo arquivo depois
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem');
      return;
    }
    // Pré-checagem: feedback claro antes de tentar comprimir/subir (o backend
    // também rejeita >5MB, mas aqui o erro é específico).
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande (máx 5MB)');
      return;
    }
    // Comprime estilo WhatsApp: redimensiona pra 512px no maior lado + JPEG.
    // Avatar é exibido em 32-72px → não precisa subir em tamanho cheio.
    // Se a compressão falhar (imagem corrompida), sobe o original.
    let blob: Blob = file;
    try {
      const compressed = await compressImage(file, {
        maxDim: 512,
        forceJpeg: true,
        targetMaxBytes: 300 * 1024,
      });
      blob = compressed.blob;
    } catch {
      blob = file;
    }
    // Mesmo após o fallback pro original, garante o limite (evita 413 obscuro).
    if (blob.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande (máx 5MB)');
      return;
    }
    uploadAvatar.mutate(blob, {
      onSuccess: () => toast.success('Foto atualizada'),
      onError: () => toast.error('Falha ao enviar a foto'),
    });
  };
  // Alterna o tema: aplica local na hora (localStorage + DOM) e persiste no banco
  // (cross-device — logar em outro PC mantém a escolha).
  const toggleTheme = (): void => {
    const next = themeResolved === 'dark' ? 'light' : 'dark';
    setThemePref(next);
    updatePrefs.mutate({ theme: next });
  };
  const router = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [notifSettingsOpen, setNotifSettingsOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // Fecha o dropdown de status junto com o menu — senão reabre já expandido.
  useEffect(() => {
    if (!menuOpen) setStatusOpen(false);
  }, [menuOpen]);

  // Online dot: reflete estado do Socket.IO (conectado = online).
  const [online, setOnline] = useState(false);
  useEffect(() => {
    const socket = getSocket();
    setOnline(socket.connected);
    const onConnect = (): void => setOnline(true);
    const onDisconnect = (): void => setOnline(false);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  }, []);

  const handleRevokeAll = async () => {
    setMenuOpen(false);
    const ok = await confirm({
      title: 'Sair de todos os dispositivos?',
      description:
        'Você será desconectado de web, celular e outros browsers, e terá que fazer login de novo em cada um.',
      confirmText: 'Sair de todos',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await revokeAllSessionsRequest();
      toast.success('Sessões encerradas em todos os dispositivos.');
    } catch (err) {
      // Mesmo se a chamada falhar, prosseguimos com logout local — outros
      // devices podem cair com TTL natural do JWT (15min) se houver indisponibilidade.
      // biome-ignore lint/suspicious/noConsole: log diagnóstico
      console.error('revoke-all-sessions falhou', err);
    } finally {
      // NÃO chamar clearSession() antes do reload: o `window.location.href`
      // abaixo descarta todo o estado (React/Zustand/Query/socket worker), e a
      // revogação no servidor é o que de fato encerra a sessão. clearSession()
      // aqui só dispararia um re-render com user=null que pisca o error boundary
      // do TanStack Router ("Something went wrong!") por milissegundos.
      // Avisa outras abas do mesmo browser (sessão compartilhada) pra caírem já.
      broadcastLogout();
      clearPersistedQueryCache();
      window.location.href = '/login';
    }
  };

  const handleLogout = async () => {
    setMenuOpen(false);
    try {
      await logoutRequest();
    } finally {
      // Sem clearSession() — o reload total abaixo já zera todo o estado e a
      // revogação no servidor encerra a sessão. clearSession() antes do reload
      // causava o flash do error boundary do router (re-render com user=null).
      // Limpa cache do persistQueryClient — sem isso, a próxima sessão (mesmo
      // OUTRO tenant no mesmo browser) restaura dados do tenant anterior por
      // alguns segundos antes do refetch (vazamento cross-tenant na UI).
      // Avisa outras abas do mesmo browser (sessão compartilhada) pra caírem já.
      broadcastLogout();
      clearPersistedQueryCache();
      // window.location pra reload TOTAL — descarta Web Worker do socket,
      // estado React, TanStack Query, etc. Mais robusto que router.navigate
      // pra cenário pós-logout (lição #33).
      window.location.href = '/login';
    }
  };

  return (
    <header className="relative flex h-[64px] flex-shrink-0 items-center gap-3.5 border-b border-n-200 bg-n-0 pr-[16px] pl-[24px]">
      {/* Estreito: o menu vira três barrinhas À ESQUERDA, antes do logo. */}
      {compacto && <TopNav compacto />}

      {/* flex-shrink-0: o logo nunca é espremido (em janela estreita sumia). */}
      <div className="flex flex-shrink-0 items-center">
        <Logo tone={themeResolved === 'dark' ? 'dark' : 'light'} height={32} />
      </div>

      {/* Menu principal no topo (substitui o antigo Rail lateral). No estreito,
          só o espaçador que empurra chat e avatar pra direita. */}
      {compacto ? <div className="flex-1" /> : <TopNav />}

      {/* Badge de saúde dos canais — só aparece quando tem canal com problema */}
      <ChannelHealthBadge />

      {/* Chat interno — ícone com badge de não-lidas + popup ancorado */}
      <InternalChatButton />

      <div ref={menuRef} className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex items-center gap-2 rounded-md px-2.5 py-1 transition hover:bg-n-100"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <div className="relative">
            <div className="relative flex h-[32px] w-[32px] items-center justify-center overflow-hidden rounded-pill bg-n-100 text-n-400">
              <User size={20} strokeWidth={1.5} />
              {avatarUrl && (
                <img
                  key={avatarUrl}
                  src={avatarUrl}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
            </div>
            {/* Bolinha online/offline — verde só quando conectado E disponível
                (marcar-se offline deixa cinza mesmo com socket vivo). */}
            <span
              className={`-right-0.5 -bottom-0.5 absolute h-3.5 w-3.5 rounded-pill border-2 border-white ${
                online && available ? 'bg-success-500' : 'bg-n-400'
              }`}
              title={online && available ? 'Online' : 'Offline'}
              aria-label={online && available ? 'Online' : 'Offline'}
            />
          </div>
          <ChevronDown size={14} className="text-n-500" aria-hidden="true" />
        </button>

        {menuOpen && user && (
          <div
            role="menu"
            className="absolute right-0 top-12 z-50 w-64 overflow-hidden rounded-md border border-n-200 bg-n-0 shadow-lg"
          >
            <div className="flex flex-col items-center gap-2 border-b border-n-200 px-4 py-4">
              {/* Avatar grande clicável — abre o seletor de arquivo pra trocar a foto. */}
              <label className="relative cursor-pointer" title="Alterar foto">
                <div className="relative flex h-[72px] w-[72px] items-center justify-center overflow-hidden rounded-pill bg-n-100 text-n-400">
                  <User size={36} strokeWidth={1.5} />
                  {avatarUrl && (
                    <img
                      key={avatarUrl}
                      src={avatarUrl}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                </div>
                <span className="-right-0.5 -bottom-0.5 absolute flex h-6 w-6 items-center justify-center rounded-pill border-2 border-n-0 bg-brand-500 text-white">
                  {uploadAvatar.isPending ? (
                    <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                  ) : (
                    <Camera size={12} aria-hidden="true" />
                  )}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => void onPickAvatar(e)}
                  disabled={uploadAvatar.isPending}
                />
              </label>
              <div className="text-center">
                <p className="text-sm font-semibold text-n-900">{user.name}</p>
                <p className="text-xs text-n-500">{user.email}</p>
                <p className="mt-1 text-[11px] uppercase tracking-wide text-n-400">
                  {user.profile === 'admin'
                    ? 'Administrador'
                    : (user.acesso?.perfilNome ?? 'Atendente')}
                </p>
              </div>
              {user.impersonating && (
                <div className="w-full rounded-md bg-warning-50 px-2 py-1.5 text-[10px] leading-tight text-warning-700">
                  <div className="font-semibold uppercase tracking-wide">via Suporte</div>
                  <div className="mt-0.5 break-all font-mono text-warning-800">
                    {user.impersonating.email}
                  </div>
                </div>
              )}
            </div>
            {/* Seletor de disponibilidade (estilo print 2): ícone colorido +
                pill de status + dropdown com opções Online/Offline. */}
            <div className="border-b border-n-100">
              <button
                type="button"
                onClick={() => setStatusOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-sm transition hover:bg-n-50"
                aria-haspopup="true"
                aria-expanded={statusOpen}
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`flex h-7 w-7 items-center justify-center rounded-pill text-white ${
                      available ? 'bg-success-500' : 'bg-danger-500'
                    }`}
                    aria-hidden="true"
                  >
                    {available ? <UserCheck size={15} /> : <UserX size={15} />}
                  </span>
                  <span
                    className={`rounded-pill px-2.5 py-0.5 font-semibold text-[12px] ${
                      available ? 'bg-success-50 text-success-700' : 'bg-n-100 text-n-600'
                    }`}
                  >
                    {available ? 'Online' : 'Offline'}
                  </span>
                </span>
                <ChevronDown
                  size={14}
                  className={`text-n-500 transition-transform ${statusOpen ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </button>
              {statusOpen && (
                <div className="border-n-100 border-t bg-n-50">
                  <button
                    type="button"
                    onClick={() => {
                      setAvailability('online');
                      setStatusOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-n-700 transition hover:bg-n-100"
                  >
                    <span className="h-2.5 w-2.5 rounded-pill bg-success-500" aria-hidden="true" />
                    Online
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAvailability('offline');
                      setStatusOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-n-700 transition hover:bg-n-100"
                  >
                    <span className="h-2.5 w-2.5 rounded-pill bg-n-400" aria-hidden="true" />
                    Offline
                  </button>
                </div>
              )}
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={themeResolved === 'dark'}
              onClick={toggleTheme}
              className="flex w-full items-center justify-between gap-2 border-b border-n-100 px-4 py-2.5 text-sm text-n-700 transition hover:bg-n-50"
            >
              <span className="flex items-center gap-2">
                {themeResolved === 'dark' ? (
                  <Moon size={16} aria-hidden="true" />
                ) : (
                  <Sun size={16} aria-hidden="true" />
                )}
                Modo noturno
              </span>
              <span
                className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-pill transition-colors ${
                  themeResolved === 'dark' ? 'bg-brand-500' : 'bg-n-300'
                }`}
                aria-hidden="true"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-pill bg-white shadow transition-transform ${
                    themeResolved === 'dark' ? 'translate-x-[18px]' : 'translate-x-[2px]'
                  }`}
                />
              </span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                setNotifSettingsOpen(true);
              }}
              className="flex w-full items-center gap-2 border-b border-n-100 px-4 py-2.5 text-sm text-n-700 transition hover:bg-n-50"
            >
              <BellRing size={16} aria-hidden="true" />
              Notificações
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                setChangePwOpen(true);
              }}
              className="flex w-full items-center gap-2 border-b border-n-100 px-4 py-2.5 text-sm text-n-700 transition hover:bg-n-50"
            >
              <KeyRound size={16} aria-hidden="true" />
              Alterar senha
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              className="flex w-full items-center gap-2 border-b border-n-100 px-4 py-2.5 text-sm text-n-700 transition hover:bg-n-50"
            >
              <LogOut size={16} aria-hidden="true" />
              Sair
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={handleRevokeAll}
              className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-danger-600 transition hover:bg-danger-50"
            >
              <ShieldOff size={16} aria-hidden="true" />
              Sair de todos os dispositivos
            </button>
            <VersionFooter />
          </div>
        )}
      </div>

      <NotificationSettingsModal
        open={notifSettingsOpen}
        onClose={() => setNotifSettingsOpen(false)}
      />

      <ChangePasswordModal open={changePwOpen} onClose={() => setChangePwOpen(false)} />
    </header>
  );
}
